import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiRequest, ApiException } from '../../shared/hooks/useApi'
import { LoadingSpinner, ErrorDisplay } from '../../shared/components'

export default function VerifyPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isVerifying, setIsVerifying] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const hasVerified = useRef(false)

  useEffect(() => {
    const token = searchParams.get('token')
    
    if (!token) {
      setError('Invalid verification link. No token provided.')
      setIsVerifying(false)
      return
    }

    // Prevent multiple verification attempts
    if (hasVerified.current) {
      return
    }
    hasVerified.current = true

    const verifyTokenAsync = async (token: string) => {
      try {
        setIsVerifying(true)
        setError(null)

        // Verify the magic link token
        await apiRequest(`/auth/verify?token=${encodeURIComponent(token)}`)
        
        // Verification successful - redirect to dashboard
        navigate('/', { replace: true })
      } catch (error) {
        if (error instanceof ApiException) {
          if (error.status === 401) {
            setError('This magic link has expired or is invalid. Please request a new one.')
          } else if (error.status === 429) {
            setError('Too many verification attempts. Please try again later.')
          } else {
            setError(error.message || 'Verification failed. Please try again.')
          }
        } else {
          setError('Network error occurred. Please check your connection and try again.')
        }
        setIsVerifying(false)
      }
    }

    verifyTokenAsync(token)
  }, [searchParams, navigate])



  const handleReturnToLogin = () => {
    navigate('/login', { replace: true })
  }

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '1rem'
  }

  const cardStyle = {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '3rem 2rem',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center' as const
  }

  const buttonStyle = {
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    marginTop: '1rem'
  }

  if (isVerifying) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <LoadingSpinner style={{ textAlign: 'center' }} />
          <h1 style={{ margin: '1rem 0 0.5rem 0', color: '#333', fontSize: '1.5rem' }}>
            Verifying Magic Link
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
            Please wait while we verify your authentication...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ fontSize: '3rem', color: '#dc3545', marginBottom: '1rem' }}>✗</div>
        <h1 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.5rem' }}>
          Verification Failed
        </h1>
        
        {error && (
          <div style={{ margin: '1rem 0', textAlign: 'left' }}>
            <ErrorDisplay error={error} />
          </div>
        )}

        <button 
          onClick={handleReturnToLogin}
          style={buttonStyle}
        >
          Return to Login
        </button>
      </div>
    </div>
  )
}